import { Icon20StarsFilled } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generationScreenOpened, saveGeneratedImage } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.actions';
import {
    downloadAiPhotoGenerationImage,
    getAiPhotoGenerationImageFileName,
} from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.helpers';
import { useAiPhotoGenerationAnalyticData } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.hooks';
import { aiPhotoGenerationImageHelper } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.image';
import {
    getAiGenerationImage,
    getAiGenerationImageSize,
    getAiGenerationImageState,
} from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.selectors';
import { EAiPhotoGenerationState } from 'reactApp/modules/aiPhotoGeneration/aiPhotoGeneration.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { AiPhotoGenerationClamp } from 'reactApp/ui/AiPhotoGeneration/AiPhotoGenerationClamp/AiPhotoGenerationClamp';
import { AiPhotoGenerationLoader } from 'reactApp/ui/AiPhotoGeneration/Loader/AiPhotoGenerationLoader';
import { AiPhotoGenerationLoaderFinish } from 'reactApp/ui/AiPhotoGeneration/LoaderFinish/AiPhotoGenerationLoaderFinish';
import { openAiPhotoGenerationCancelGenerationModal } from 'reactApp/ui/AiPhotoGeneration/Modals/CancelGeneration/AiPhotoGenerationCancelGenerationModal.helpers';
import {
    getAiPhotoGenerationMailSpacePromoModalShown,
    renderAiPhotoGenerationMailSpacePromoModal,
    setAiPhotoGenerationMailSpacePromoModalShown,
} from 'reactApp/ui/AiPhotoGeneration/Promo/Modal/MailSpacePromo/AiPhotoGenerationMailSpacePromo.helpers';
import { AiPhotoGenerationToolbarComplete } from 'reactApp/ui/AiPhotoGeneration/ToolbarComplete/AiPhotoGenerationToolbarComplete';
import { AiPhotoGenerationToolbarIdle } from 'reactApp/ui/AiPhotoGeneration/ToolbarIdle/AiPhotoGenerationToolbarIdle';
import { MenuItemName } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton.types';
import { ScreenNew } from 'reactApp/ui/ScreenNew/ScreenNew';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './FullScreenAiPhotoGenerationModal.css';

export interface IFullScreenAiPhotoGenerationModal {
    onClose?: () => void;
}

// eslint-disable-next-line max-lines-per-function
export const FullScreenAiPhotoGenerationModal = memo<IFullScreenAiPhotoGenerationModal>(({ onClose }) => {
    const state = useSelector(getAiGenerationImageState);
    const image = useSelector(getAiGenerationImage);
    const size = useSelector(getAiGenerationImageSize);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const isCorpUser = useSelector(UserSelectors.isCorpUser);
    const storage = useSelector(getCurrentStorage);
    const dispatch = useDispatch();
    const analyticsData = useAiPhotoGenerationAnalyticData();

    useEffect(() => {
        dispatch(generationScreenOpened());
    }, [dispatch]);

    useEffect(() => {
        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: 'gen-screen-show',
            source: storage,
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user', 'source']],
        });
    }, [storage]);

    const handleClose = useCallback(() => {
        sendPaymentGa({
            eventCategory: ECategoryGa.aiPhotoGeneration,
            action: 'gen-screen-close',
            source: storage,
            ...analyticsData,
            dwh_enabled: true,
            dwh_enabled_fields: [['paid_user', 'corp_user', 'source']],
        });

        if (state === EAiPhotoGenerationState.proccess) {
            openAiPhotoGenerationCancelGenerationModal();
            return;
        }

        onClose?.();
    }, [analyticsData, onClose, state, storage]);

    const content = useMemo(() => {
        if (state === EAiPhotoGenerationState.idle) {
            return <Icon20StarsFilled className={styles.idleIcon} width={116} height={116} data-test-id="idle-icon" />;
        } else if (state === EAiPhotoGenerationState.proccess) {
            return <AiPhotoGenerationLoader />;
        } else if (state === EAiPhotoGenerationState.endAnimation) {
            return <AiPhotoGenerationLoaderFinish />;
        } else if (state === EAiPhotoGenerationState.clamp) {
            return <AiPhotoGenerationClamp onClose={handleClose} />;
        }

        return <img src={image} className={styles.generatedImage} alt="Сгенерированное изображение" />;
    }, [state, image, handleClose]);

    const toolbar = useMemo(() => {
        if (state === EAiPhotoGenerationState.complete) {
            return <AiPhotoGenerationToolbarComplete />;
        } else if (state === EAiPhotoGenerationState.idle) {
            return <AiPhotoGenerationToolbarIdle />;
        }
    }, [state]);

    const titleText = useMemo(() => {
        if (state === EAiPhotoGenerationState.complete) {
            return 'Сгенерированное изображение';
        }

        return 'Генерация изображения с нейросетью';
    }, [state]);

    const infoText = useMemo(() => {
        if (state === EAiPhotoGenerationState.complete) {
            return size?.value;
        }
    }, [size, state]);

    const options = useMemo(
        () => [
            {
                name: MenuItemName.SHARE,
                onClick: async () => {
                    dispatch(
                        saveGeneratedImage({
                            publishAfterSuccess: true,
                        })
                    );

                    sendPaymentGa({
                        eventCategory: ECategoryGa.aiPhotoGeneration,
                        action: 'share-btn-click',
                        ...analyticsData,
                        dwh_enabled: true,
                        dwh_enabled_fields: [['paid_user', 'corp_user']],
                    });
                },
            },
            {
                name: MenuItemName.DOWNLOAD,
                onClick: () => {
                    const data = aiPhotoGenerationImageHelper.getImageData();
                    if (data) {
                        const fileName = `${getAiPhotoGenerationImageFileName()}.jpg`;
                        downloadAiPhotoGenerationImage(data, fileName);

                        if (!isCorpUser && !isPaidUser) {
                            const mailSpacePromoShown = dispatch(getAiPhotoGenerationMailSpacePromoModalShown());

                            if (!mailSpacePromoShown) {
                                dispatch(setAiPhotoGenerationMailSpacePromoModalShown());
                                renderAiPhotoGenerationMailSpacePromoModal();
                            }
                        }

                        sendPaymentGa({
                            eventCategory: ECategoryGa.aiPhotoGeneration,
                            action: 'download-btn-click',
                            ...analyticsData,
                            dwh_enabled: true,
                            dwh_enabled_fields: [['paid_user', 'corp_user']],
                        });
                    }
                },
            },
        ],
        [analyticsData, dispatch, isCorpUser, isPaidUser]
    );

    return (
        <ScreenNew
            onClose={handleClose}
            closable={state !== EAiPhotoGenerationState.endAnimation}
            withHeader={true}
            withLogo={true}
            className={classNames(styles.screenRoot, {
                [styles.screenRootClamp]: state === EAiPhotoGenerationState.clamp,
            })}
            contentClassName={styles.screenContent}
            title={titleText}
            info={infoText}
            showOptions={state === EAiPhotoGenerationState.complete}
            options={options}
            isDarkTheme
        >
            <div className={styles.root} data-test-id="fullscreen-ai-photo-generation">
                <div className={styles.mainContent} data-test-id="main-content">
                    {content}
                </div>

                {toolbar}
            </div>
        </ScreenNew>
    );
});

FullScreenAiPhotoGenerationModal.displayName = 'FullScreenAiPhotoGeneration';
